"use client";

/**
 * Décors visuels (purement graphiques) des cartes MagicBento de la promesse
 * KS Rent Services. Extraits de ValuePropsSection pour garder ce dernier sous
 * 400 lignes. Aucun texte localisable ici : le seul libellé (alt de l'image
 * évènementielle) est passé en paramètre. Les libellés des moyens de paiement
 * sont des marques (Visa, Mastercard…) → identiques FR/EN.
 */
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Image, { type StaticImageData } from "next/image";
import { Marquee } from "@/components/magicui/marquee";
import { Icon } from "@/components/ui/Icon";
import cannesVisual from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

export const cardBackdropByTheme = (
  theme: "payment" | "experience" | "support" | "events" | "security",
  eventsAlt = "Ambiance évènementielle",
) => {
  const common =
    "pointer-events-none absolute inset-0 overflow-hidden rounded-[20px]";

  if (theme === "payment") {
    return (
      <div className={common}>
        <motion.div
          animate={{ opacity: [0.22, 0.38, 0.22], scale: [1, 1.05, 1] }}
          transition={{ duration: 5.5, repeat: Infinity }}
          className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/35 blur-2xl"
        />
        <div className="absolute inset-0 bg-[linear-gradient(130deg,rgba(255,255,255,0.10),transparent_45%)]" />
      </div>
    );
  }

  if (theme === "experience") {
    return (
      <div className={common}>
        <div className="bg-primary/14 absolute -right-12 top-8 h-32 w-32 rounded-full blur-3xl" />
        <div className="absolute -left-10 bottom-6 h-28 w-28 rounded-full bg-white/10 blur-3xl" />
        <div className="from-black/8 absolute inset-0 bg-gradient-to-b via-black/20 to-black/55" />
      </div>
    );
  }

  if (theme === "support") {
    return (
      <div className={common}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_20%,rgba(196,153,45,0.20),transparent_40%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/45" />
      </div>
    );
  }

  if (theme === "events") {
    return (
      <div className={common}>
        <div className="absolute inset-0">
          <Image
            src={cannesVisual}
            alt={eventsAlt}
            fill
            className="object-cover opacity-35"
            sizes="(max-width: 768px) 100vw, 33vw"
          />
        </div>
        <div className="to-black/68 absolute inset-0 bg-gradient-to-b from-black/30 via-black/45" />
      </div>
    );
  }

  return (
    <div className={common}>
      <div className="absolute -left-8 top-10 h-24 w-24 rounded-full bg-primary/20 blur-2xl" />
      <div className="absolute inset-x-0 top-[46%] h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div className="absolute inset-0 rounded-[20px] border border-white/10" />
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/55" />
    </div>
  );
};

export function VehicleAutoSliderBackground({
  images,
  alt = "Flotte KS Rent Services",
}: {
  images: StaticImageData[];
  alt?: string;
}) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 2300);
    return () => window.clearInterval(timer);
  }, [images]);

  if (!images.length) return null;

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[20px]">
      <AnimatePresence mode="sync">
        <motion.div
          key={index}
          initial={false}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <motion.div
            initial={{ scale: 1.04, y: 6 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ duration: 2.3, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <Image
              src={images[index]}
              alt={alt}
              fill
              className="opacity-62 object-cover"
              sizes="(max-width: 768px) 100vw, 33vw"
            />
          </motion.div>
        </motion.div>
      </AnimatePresence>
      <div className="from-black/18 via-black/24 to-black/48 absolute inset-0 bg-gradient-to-b" />
      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/40 to-transparent" />
    </div>
  );
}

export function PaymentMethodsBackground() {
  const methods = [
    { icon: "simple-icons:visa", label: "Visa" },
    { icon: "simple-icons:mastercard", label: "Mastercard" },
    { icon: "simple-icons:americanexpress", label: "Amex" },
    { icon: "simple-icons:applepay", label: "Apple Pay" },
    { icon: "simple-icons:googlepay", label: "Google Pay" },
    { icon: "simple-icons:paypal", label: "PayPal" },
  ];

  return (
    <div className="pointer-events-none absolute inset-x-3 top-[2.95rem] z-10 overflow-hidden">
      <Marquee
        pauseOnHover={false}
        repeat={3}
        className="p-0 [--duration:15s] [--gap:0.4rem]"
      >
        {methods.map((method) => (
          <span
            key={method.label}
            className="inline-flex items-center gap-1 rounded-full border border-white/35 bg-black/55 px-2.5 py-0.5 text-[9px] uppercase tracking-[0.1em] text-white/95"
          >
            <Icon icon={method.icon} width={10} height={10} />
            {method.label}
          </span>
        ))}
      </Marquee>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-7 bg-gradient-to-r from-black/90 to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-7 bg-gradient-to-l from-black/90 to-transparent" />
    </div>
  );
}

export function SupportIconBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 z-10">
      <div className="absolute left-1/2 top-[43%] flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/45 shadow-[0_0_28px_rgba(196,153,45,0.28)]">
        <Icon
          icon="mdi:headset"
          width={30}
          height={30}
          className="text-white/90"
        />
      </div>
    </div>
  );
}

export function SecurityTrustBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 z-10">
      <div className="absolute left-1/2 top-[42%] flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/45 shadow-[0_0_24px_rgba(196,153,45,0.18)]">
        <Icon
          icon="mdi:shield-lock-outline"
          width={28}
          height={28}
          className="text-white/90"
        />
      </div>
    </div>
  );
}
